/**
 * Global Filter Context
 * Provides persistent filter state across all pages.
 *
 * Persistence strategy (priority order):
 *   1. URL search params (highest priority - allows bookmarking / sharing)
 *   2. localStorage (fallback when URL has no filter params)
 *
 * On every filter change both URL and localStorage are updated.
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';

// ---- Types ----

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

export interface GlobalFilters {
  selectedCountry: string | null;
  selectedBrand: string | null;
  selectedRetailer: string | null;
  selectedCategory: string | null;
  selectedCity: string | null;
  selectedStoreId: string | null;
  dateRange: DateRange;
}

export type FilterKey = keyof Omit<GlobalFilters, 'dateRange'>;

export interface FilterContextType extends GlobalFilters {
  /** Set a single scalar filter by key */
  setFilter: (key: FilterKey, value: string | null) => void;
  /** Clear a single filter by key */
  clearFilter: (key: FilterKey) => void;
  /** Clear all filters (including date range) */
  clearAllFilters: () => void;

  // Convenience setters (kept for backward-compat with existing code)
  setSelectedCountry: (country: string | null) => void;
  setSelectedBrand: (brand: string | null) => void;
  setSelectedRetailer: (retailer: string | null) => void;
  setSelectedCategory: (category: string | null) => void;
  setSelectedCity: (city: string | null) => void;
  setSelectedStoreId: (storeId: string | null) => void;
  setDateRange: (range: DateRange) => void;

  /** Legacy alias */
  clearAll: () => void;

  /** Number of currently active individual filter values */
  activeFilterCount: number;
}

// ---- Constants ----

const STORAGE_KEY = 'iris_global_filters';

/** URL param names (short keys for cleaner URLs) */
const URL_KEYS = {
  selectedCountry: 'country',
  selectedBrand: 'brand',
  selectedRetailer: 'retailer',
  selectedCategory: 'category',
  selectedCity: 'city',
  selectedStoreId: 'store',
  dateRangeStart: 'from',
  dateRangeEnd: 'to',
} as const;

const defaultFilters: GlobalFilters = {
  selectedCountry: null,
  selectedBrand: null,
  selectedRetailer: null,
  selectedCategory: null,
  selectedCity: null,
  selectedStoreId: null,
  dateRange: { start: null, end: null },
};

// ---- LocalStorage helpers ----

function loadFromStorage(): GlobalFilters {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultFilters;

    const parsed = JSON.parse(raw);
    return {
      selectedCountry: parsed.selectedCountry ?? null,
      selectedBrand: parsed.selectedBrand ?? null,
      selectedRetailer: parsed.selectedRetailer ?? null,
      selectedCategory: parsed.selectedCategory ?? null,
      selectedCity: parsed.selectedCity ?? null,
      selectedStoreId: parsed.selectedStoreId ?? null,
      dateRange: {
        start: parsed.dateRange?.start ? new Date(parsed.dateRange.start) : null,
        end: parsed.dateRange?.end ? new Date(parsed.dateRange.end) : null,
      },
    };
  } catch {
    return defaultFilters;
  }
}

function saveToStorage(filters: GlobalFilters): void {
  try {
    const serializable = {
      ...filters,
      dateRange: {
        start: filters.dateRange.start?.toISOString() ?? null,
        end: filters.dateRange.end?.toISOString() ?? null,
      },
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(serializable));
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

// ---- URL <-> Filters helpers ----

function filtersFromURL(params: URLSearchParams): GlobalFilters | null {
  // Check if ANY filter param is present in the URL
  const hasAny = Object.values(URL_KEYS).some((key) => params.has(key));
  if (!hasAny) return null;

  const str = (key: string): string | null => params.get(key) || null;
  const date = (key: string): Date | null => {
    const v = params.get(key);
    if (!v) return null;
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  };

  return {
    selectedCountry: str(URL_KEYS.selectedCountry),
    selectedBrand: str(URL_KEYS.selectedBrand),
    selectedRetailer: str(URL_KEYS.selectedRetailer),
    selectedCategory: str(URL_KEYS.selectedCategory),
    selectedCity: str(URL_KEYS.selectedCity),
    selectedStoreId: str(URL_KEYS.selectedStoreId),
    dateRange: {
      start: date(URL_KEYS.dateRangeStart),
      end: date(URL_KEYS.dateRangeEnd),
    },
  };
}

function filtersToURLParams(filters: GlobalFilters): Record<string, string> {
  const params: Record<string, string> = {};

  if (filters.selectedCountry) params[URL_KEYS.selectedCountry] = filters.selectedCountry;
  if (filters.selectedBrand) params[URL_KEYS.selectedBrand] = filters.selectedBrand;
  if (filters.selectedRetailer) params[URL_KEYS.selectedRetailer] = filters.selectedRetailer;
  if (filters.selectedCategory) params[URL_KEYS.selectedCategory] = filters.selectedCategory;
  if (filters.selectedCity) params[URL_KEYS.selectedCity] = filters.selectedCity;
  if (filters.selectedStoreId) params[URL_KEYS.selectedStoreId] = filters.selectedStoreId;
  if (filters.dateRange.start) params[URL_KEYS.dateRangeStart] = filters.dateRange.start.toISOString().slice(0, 10);
  if (filters.dateRange.end) params[URL_KEYS.dateRangeEnd] = filters.dateRange.end.toISOString().slice(0, 10);

  return params;
}

// ---- Context ----

const FilterContext = createContext<FilterContextType | undefined>(undefined);

// ---- Provider ----

export const FilterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // We may not be inside a Router yet (e.g. on the /login route rendered outside Layout).
  // useSearchParams will throw if there is no router. We wrap the inner provider
  // so that it only activates URL sync when inside a Router.
  return (
    <FilterProviderInner>{children}</FilterProviderInner>
  );
};

/**
 * Inner provider that does the heavy lifting.
 * It attempts to use useSearchParams; if unavailable it falls back to localStorage-only mode.
 */
const FilterProviderInner: React.FC<{ children: ReactNode }> = ({ children }) => {
  // --- Attempt to use search params ---
  let searchParams: URLSearchParams | null = null;
  let setSearchParams: ((params: URLSearchParams, opts?: { replace?: boolean }) => void) | null = null;

  try {
    // This hook throws if not inside a <Router>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [sp, ssp] = useSearchParams();
    searchParams = sp;
    setSearchParams = ssp as any;
  } catch {
    // Not inside a Router - URL sync disabled
  }

  // --- Initial state: URL first, then localStorage fallback ---
  const [filters, setFilters] = useState<GlobalFilters>(() => {
    if (searchParams) {
      const fromURL = filtersFromURL(searchParams);
      if (fromURL) {
        // Also persist URL filters to localStorage immediately
        saveToStorage(fromURL);
        return fromURL;
      }
    }
    return loadFromStorage();
  });

  // Debounce timer ref for URL updates
  const urlUpdateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Persist to localStorage whenever filters change
  useEffect(() => {
    saveToStorage(filters);
  }, [filters]);

  // Sync filters -> URL (debounced to avoid excessive history entries)
  useEffect(() => {
    if (!setSearchParams) return;

    if (urlUpdateTimer.current) {
      clearTimeout(urlUpdateTimer.current);
    }

    urlUpdateTimer.current = setTimeout(() => {
      const newParams = filtersToURLParams(filters);

      // Preserve any non-filter URL params already present
      const currentParams = new URLSearchParams(window.location.search);
      const filterParamNames = new Set(Object.values(URL_KEYS));

      // Build a new URLSearchParams keeping non-filter params
      const merged = new URLSearchParams();
      currentParams.forEach((value, key) => {
        if (!filterParamNames.has(key as any)) {
          merged.set(key, value);
        }
      });
      // Add filter params
      Object.entries(newParams).forEach(([key, value]) => {
        merged.set(key, value);
      });

      setSearchParams!(merged, { replace: true });
    }, 300); // 300ms debounce

    return () => {
      if (urlUpdateTimer.current) clearTimeout(urlUpdateTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // --- Actions ---

  const setFilter = useCallback((key: FilterKey, value: string | null) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearFilter = useCallback((key: FilterKey) => {
    setFilters((prev) => ({ ...prev, [key]: null }));
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  // Convenience setters
  const setSelectedCountry = useCallback((country: string | null) => {
    setFilters((prev) => ({ ...prev, selectedCountry: country }));
  }, []);

  const setSelectedBrand = useCallback((brand: string | null) => {
    setFilters((prev) => ({ ...prev, selectedBrand: brand }));
  }, []);

  const setSelectedRetailer = useCallback((retailer: string | null) => {
    setFilters((prev) => ({ ...prev, selectedRetailer: retailer }));
  }, []);

  const setSelectedCategory = useCallback((category: string | null) => {
    setFilters((prev) => ({ ...prev, selectedCategory: category }));
  }, []);

  const setSelectedCity = useCallback((city: string | null) => {
    setFilters((prev) => ({ ...prev, selectedCity: city }));
  }, []);

  const setSelectedStoreId = useCallback((storeId: string | null) => {
    setFilters((prev) => ({ ...prev, selectedStoreId: storeId }));
  }, []);

  const setDateRange = useCallback((range: DateRange) => {
    setFilters((prev) => ({ ...prev, dateRange: range }));
  }, []);

  // --- Derived ---

  const activeFilterCount =
    (filters.selectedCountry ? 1 : 0) +
    (filters.selectedBrand ? 1 : 0) +
    (filters.selectedRetailer ? 1 : 0) +
    (filters.selectedCategory ? 1 : 0) +
    (filters.selectedCity ? 1 : 0) +
    (filters.selectedStoreId ? 1 : 0) +
    (filters.dateRange.start || filters.dateRange.end ? 1 : 0);

  const value: FilterContextType = {
    ...filters,
    setFilter,
    clearFilter,
    clearAllFilters,
    setSelectedCountry,
    setSelectedBrand,
    setSelectedRetailer,
    setSelectedCategory,
    setSelectedCity,
    setSelectedStoreId,
    setDateRange,
    clearAll: clearAllFilters, // legacy alias
    activeFilterCount,
  };

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
};

// ---- Hooks ----

/**
 * Primary hook to access global filter state and actions.
 */
export const useFilters = (): FilterContextType => {
  const context = useContext(FilterContext);
  if (context === undefined) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
};

/**
 * Legacy alias - kept for backward compatibility with existing components.
 */
export const useGlobalFilters = useFilters;

export default FilterContext;
